import { WorkflowStepDefinition } from '@calm/evexia-web-sdk';
import { useRestartWorkflow } from '@rhg/hooks/use-restart-workflow';
import { useMutation } from '@rhg/libs/react-query';
import {
    STORED_RETAKING_SURVEY_KEY,
    clearFtueSessionStorage,
} from '@rhg/utils/workflows';

export interface RestartFtueWorkflowParams {
    workflowDefnId: string;
    onStepUpdate: (step: WorkflowStepDefinition | undefined) => void;
    onRetakingSurveyUpdate: (isRetaking: boolean) => void;
    clearFtueVideoCache: () => Promise<void>;
}

// Mutation hook for restarting FTUE workflow
export function useRestartFtueWorkflow() {
    const { restartWorkflowAsync } = useRestartWorkflow();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (params: RestartFtueWorkflowParams) => {
            const {
                workflowDefnId,
                onStepUpdate,
                onRetakingSurveyUpdate,
                clearFtueVideoCache,
            } = params;

            const resetResponse = await restartWorkflowAsync({
                workflowDefnId,
            });
            clearFtueSessionStorage();
            onStepUpdate(resetResponse.data?.stepDefn);
            onRetakingSurveyUpdate(true);
            sessionStorage.setItem(STORED_RETAKING_SURVEY_KEY, 'true');
            await clearFtueVideoCache();
            // Return the resolved step so callers can detect a restart that
            // landed on the terminal step (e.g. journaling with no restartable
            // instance) and recover by starting the workflow fresh.
            return resetResponse.data?.stepDefn?.defnId;
        },
    });

    return {
        restartFtueWorkflow: mutate,
        restartFtueWorkflowAsync: mutateAsync,
        ...otherProps,
    };
}
